import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useClients } from '@/hooks/useClients';
import { useUnifiedCampaigns } from '@/hooks/useUnifiedCampaigns';
import { useAuth } from '@/contexts/AuthContext';
import { Phone, Users, CheckCircle, Search } from 'lucide-react';
import type { Database } from '@/integrations/supabase/types';

type Client = Database['public']['Tables']['clients']['Row'];

interface Campaign {
  id: string;
  name: string;
  description: string;
  campaign_type: string;
  status: string;
  client_id?: string;
  user_id: string;
  created_at: string;
}

interface CampaignsManagementProps {
  adminMode?: boolean;
}

export default function CampaignsManagement({ adminMode = false }: CampaignsManagementProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [allClients, setAllClients] = useState<Client[]>([]);
  const [ownerEmails, setOwnerEmails] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'name' | 'client' | 'status' | 'type' | 'owner'>(
    'name',
  );
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const { clients } = useClients();
  const { updateCampaign } = useUnifiedCampaigns();
  const { user, isAdmin } = useAuth();
  const { toast } = useToast();

  // Only use admin features when explicitly in admin mode AND user is actually an admin
  const showAdminFeatures = adminMode && isAdmin;

  useEffect(() => {
    fetchCampaigns();
    if (showAdminFeatures) {
      fetchAllClients();
      fetchOwnerProfiles();
    }
  }, [user, showAdminFeatures]);

  const fetchCampaigns = async () => {
    if (!user) return;

    try {
      setLoading(true);
      let query = supabase
        .from('campaigns')
        .select('*')
        .order('created_at', { ascending: false });

      // In user mode, only show campaigns owned by this user
      if (!showAdminFeatures) {
        query = query.eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;
      setCampaigns(data || []);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast({
        title: 'Error',
        description: 'Failed to load campaigns',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchAllClients = async () => {
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('name', { ascending: true });
      if (error) throw error;
      setAllClients(data || []);
    } catch (error) {
      console.error('Error fetching all clients:', error);
    }
  };

  const fetchOwnerProfiles = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('user_id, email');
      if (error) throw error;
      const map: Record<string, string> = {};
      for (const p of data || []) {
        map[p.user_id] = p.email || p.user_id;
      }
      setOwnerEmails(map);
    } catch (error) {
      console.error('Error fetching owner profiles:', error);
    }
  };

  const updateCampaignClient = async (
    campaignId: string,
    clientId: string | null,
  ) => {
    // Security check: in user mode, can only assign to clients the user owns
    if (!showAdminFeatures && clientId && !clients.some((c) => c.id === clientId)) {
      toast({
        title: 'Access Denied',
        description:
          'You can only assign campaigns to clients you have access to.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setUpdating(campaignId);

      // Build the DB update payload
      const dbUpdate: Record<string, unknown> = {
        client_id: clientId,
        updated_at: new Date().toISOString(),
      };

      // In admin mode, transfer campaign ownership to the portal owner who owns that client
      let transferredToUserId: string | undefined;
      if (showAdminFeatures && clientId) {
        const targetClient = allClients.find((c) => c.id === clientId);
        if (targetClient?.user_id) {
          dbUpdate.user_id = targetClient.user_id;
          transferredToUserId = targetClient.user_id;
        }
      }

      // Use direct Supabase call so the update is not filtered through the
      // current user's campaign scope and doesn't trigger an admin-scoped refetch
      const { error } = await supabase
        .from('campaigns')
        .update(dbUpdate)
        .eq('id', campaignId);

      if (error) throw error;

      // Update local state
      setCampaigns((prev) =>
        prev.map((campaign) =>
          campaign.id === campaignId
            ? {
                ...campaign,
                client_id: clientId || undefined,
                ...(transferredToUserId ? { user_id: transferredToUserId } : {}),
              }
            : campaign,
        ),
      );

      toast({
        title: 'Success',
        description: transferredToUserId
          ? 'Campaign assigned and ownership transferred to the portal owner'
          : 'Campaign client assignment updated',
      });
    } catch (error) {
      console.error('Error updating campaign:', error);
      toast({
        title: 'Error',
        description: 'Failed to update campaign client assignment',
        variant: 'destructive',
      });
    } finally {
      setUpdating(null);
    }
  };

  // useClients() already returns only the current user's own clients (filtered by user_id),
  // so no additional clientIds filtering is needed for non-admin users
  const availableClients = showAdminFeatures ? allClients : clients;

  const getClientName = (clientId?: string) => {
    if (!clientId) return 'No Client Assigned';
    const client = availableClients.find((c) => c.id === clientId);
    return client?.name || 'Unknown Client';
  };

  // Unique owners for the filter dropdown (admin only)
  const uniqueOwners = useMemo(() => {
    const seen = new Set<string>();
    return campaigns
      .map((c) => c.user_id)
      .filter((id) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [campaigns]);

  const filteredCampaigns = useMemo(() => {
    let result = [...campaigns];

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter((c) => c.name.toLowerCase().includes(q));
    }

    if (showAdminFeatures && ownerFilter !== 'all') {
      result = result.filter((c) => c.user_id === ownerFilter);
    }

    return result.sort((a, b) => {
      let aValue: string;
      let bValue: string;

      switch (sortBy) {
        case 'client':
          aValue = getClientName(a.client_id);
          bValue = getClientName(b.client_id);
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        case 'type':
          aValue = a.campaign_type;
          bValue = b.campaign_type;
          break;
        case 'owner':
          aValue = ownerEmails[a.user_id] || a.user_id;
          bValue = ownerEmails[b.user_id] || b.user_id;
          break;
        case 'name':
        default:
          aValue = a.name;
          bValue = b.name;
          break;
      }

      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });
  }, [campaigns, searchQuery, ownerFilter, sortBy, sortOrder, ownerEmails]);

  const toggleSort = (field: typeof sortBy) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/10 text-green-700 border-green-500/20';
      case 'paused':
        return 'bg-yellow-500/10 text-yellow-700 border-yellow-500/20';
      case 'completed':
        return 'bg-blue-500/10 text-blue-700 border-blue-500/20';
      case 'cancelled':
        return 'bg-red-500/10 text-red-700 border-red-500/20';
      default:
        return 'bg-gray-500/10 text-gray-700 border-gray-500/20';
    }
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center p-8'>
        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <Phone className='h-5 w-5' />
            Campaign Client Assignment
          </CardTitle>
          <CardDescription>
            Assign campaigns to clients so their call actions appear in client portals.
            {showAdminFeatures ? (
              <> Showing all campaigns across all accounts. Assigning a campaign to a client will transfer ownership to that client's account.</>
            ) : (
              <> Only your campaigns are shown, and you can only assign to your own clients.</>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className='space-y-4'>
            {/* Search + owner filter (admin) or just search (non-admin) */}
            <div className='flex items-center gap-2'>
              <div className='relative flex-1 max-w-sm'>
                <Search className='absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground' />
                <Input
                  placeholder='Search campaigns...'
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className='pl-8'
                />
              </div>
              {showAdminFeatures && (
                <Select value={ownerFilter} onValueChange={setOwnerFilter}>
                  <SelectTrigger className='w-52'>
                    <SelectValue placeholder='Filter by owner' />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value='all'>All accounts</SelectItem>
                    {uniqueOwners.map((uid) => (
                      <SelectItem key={uid} value={uid}>
                        {ownerEmails[uid] || uid}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Sort Controls */}
            <div className='flex items-center gap-2 text-sm flex-wrap'>
              <span className='text-muted-foreground'>Sort by:</span>
              {(['name', 'client', 'status', 'type'] as const).map((field) => (
                <Button
                  key={field}
                  variant={sortBy === field ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => toggleSort(field)}
                >
                  {field.charAt(0).toUpperCase() + field.slice(1)}
                  {sortBy === field && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                </Button>
              ))}
              {showAdminFeatures && (
                <Button
                  variant={sortBy === 'owner' ? 'default' : 'outline'}
                  size='sm'
                  onClick={() => toggleSort('owner')}
                >
                  Owner{sortBy === 'owner' && (sortOrder === 'asc' ? ' ↑' : ' ↓')}
                </Button>
              )}
            </div>

            {filteredCampaigns.map((campaign) => (
              <div
                key={campaign.id}
                className='flex items-center justify-between p-4 border rounded-lg'
              >
                <div className='flex-1'>
                  <div className='flex items-center gap-2 mb-1'>
                    <h3 className='font-medium'>{campaign.name}</h3>
                    <Badge
                      variant='outline'
                      className={getStatusColor(campaign.status)}
                    >
                      {campaign.status}
                    </Badge>
                    <Badge variant='outline'>{campaign.campaign_type}</Badge>
                  </div>
                  {campaign.description && (
                    <p className='text-sm text-muted-foreground mb-1'>
                      {campaign.description}
                    </p>
                  )}
                  <div className='flex items-center gap-4 text-sm text-muted-foreground'>
                    <div className='flex items-center gap-1'>
                      <Users className='h-3 w-3' />
                      <span>{getClientName(campaign.client_id)}</span>
                    </div>
                    {showAdminFeatures && (
                      <span className='text-xs'>
                        Owner: {ownerEmails[campaign.user_id] || campaign.user_id}
                      </span>
                    )}
                  </div>
                </div>

                <div className='flex items-center gap-2'>
                  <Select
                    value={campaign.client_id || 'none'}
                    onValueChange={(value) =>
                      updateCampaignClient(
                        campaign.id,
                        value === 'none' ? null : value,
                      )
                    }
                    disabled={updating === campaign.id}
                  >
                    <SelectTrigger className='w-52'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value='none'>No Client</SelectItem>
                      {availableClients.map((client) => (
                        <SelectItem key={client.id} value={client.id}>
                          {client.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {updating === campaign.id && (
                    <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-primary'></div>
                  )}
                </div>
              </div>
            ))}

            {filteredCampaigns.length === 0 && (
              <div className='text-center py-8 text-muted-foreground'>
                {searchQuery || ownerFilter !== 'all'
                  ? 'No campaigns match your filters'
                  : 'No campaigns found'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <CheckCircle className='h-5 w-5' />
            How Client Portals Work
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='space-y-2 text-sm text-muted-foreground'>
            <p>
              • Client portals filter call actions by the campaign's assigned
              client
            </p>
            <p>
              • Only campaigns with a client_id will show actions in that
              client's portal
            </p>
            <p>
              • Campaigns without client assignment are considered "unassigned"
              and won't appear in any client portal
            </p>
            <p>
              • Use this tool to assign existing campaigns to clients so their
              historical call data becomes visible
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
